export type BondGlyphVariant =
  | 'single'
  | 'double'
  | 'triple'
  | 'quadruple'
  | 'quintuple'
  | 'sextuple'
  | 'crossed-double'
  | 'dative'
  | 'solid-wedge'
  | 'hashed-wedge'
  | 'chain'
  | 'hydrogen'
  | 'wavy'
  | 'hapto'
  | 'agostic';

interface BondGlyphProps {
  readonly variant: BondGlyphVariant;
}

// Bespoke bond marks for the left-rail Bonds group. Chemistry glyphs stay hand-drawn rather than
// borrowing Material Symbols (see docs/icon-language.md) -- same rationale and 0 0 32 32 space as
// RingIcon. Colour follows `currentColor`; the parent control carries the accessible name.
export function BondGlyph({ variant }: BondGlyphProps) {
  return (
    <svg className="bond-glyph" viewBox="0 0 32 32" aria-hidden="true" focusable="false" data-variant={variant}>
      {variant === 'single' ? <line x1="8" y1="8" x2="24" y2="24" /> : null}
      {variant === 'double' ? (
        <>
          <line x1="6" y1="10" x2="22" y2="26" />
          <line x1="10" y1="6" x2="26" y2="22" />
        </>
      ) : null}
      {/* #418 -- two near-parallel bond lines that swap sides end-to-end, crossing once at the
          centre: a long, shallow bow-tie along the bond axis, the way
          `createBondSegments(..., crossed=true)` renders a cis/trans-unspecified C=C. Not a
          centred square ✕. */}
      {variant === 'crossed-double' ? (
        <>
          <line x1="3" y1="6" x2="29" y2="26" />
          <line x1="6" y1="3" x2="26" y2="29" />
        </>
      ) : null}
      {variant === 'triple' ? (
        <>
          <line x1="8" y1="8" x2="24" y2="24" />
          <line x1="4.5" y1="11.5" x2="20.5" y2="27.5" />
          <line x1="11.5" y1="4.5" x2="27.5" y2="20.5" />
        </>
      ) : null}
      {/* ADR 0024 (#1127): four lines, closer and finer, as the paper draws a quadruple bond. */}
      {variant === 'quadruple' ? (
        <g strokeWidth="1.6">
          <line x1="4.25" y1="11.75" x2="20.25" y2="27.75" />
          <line x1="6.75" y1="9.25" x2="22.75" y2="25.25" />
          <line x1="9.25" y1="6.75" x2="25.25" y2="22.75" />
          <line x1="11.75" y1="4.25" x2="27.75" y2="20.25" />
        </g>
      ) : null}
      {/* #1130: five and six lines, finer still. */}
      {variant === 'quintuple' ? (
        <g strokeWidth="1.3">
          {[-4, -2, 0, 2, 4].map((offset) => (
            <line key={offset} x1={8 + offset} y1={8 - offset} x2={24 + offset} y2={24 - offset} />
          ))}
        </g>
      ) : null}
      {variant === 'sextuple' ? (
        <g strokeWidth="1.1">
          {[-4.25, -2.55, -0.85, 0.85, 2.55, 4.25].map((offset) => (
            <line key={offset} x1={8 + offset} y1={8 - offset} x2={24 + offset} y2={24 - offset} />
          ))}
        </g>
      ) : null}
      {variant === 'dative' ? (
        <>
          <line x1="7" y1="7" x2="22" y2="22" />
          <polygon points="25,25 15,22.5 22.5,15" />
        </>
      ) : null}
      {/* ADR 0022 D4 / D5 -- a dashed hydrogen bond, and a wavy bond (undefined stereo), drawn like
          the paper draws them. */}
      {variant === 'hydrogen' ? <line x1="7" y1="7" x2="25" y2="25" strokeDasharray="3 3" /> : null}
      {variant === 'wavy' ? <path d="M6 6 q4 0 4 4 t4 4 t4 4 t4 4 t4 4" fill="none" /> : null}
      {/* ADR 0026 D2 -- a hapto bond: one line from the metal to the middle of a ring. */}
      {variant === 'hapto' ? (
        <>
          <polygon points="20,4 27.6,9.5 24.7,18.4 15.3,18.4 12.4,9.5" fill="none" />
          <line x1="6" y1="28" x2="20" y2="12" />
        </>
      ) : null}
      {/* ADR 0026 D6 -- an agostic interaction: a C–H bond, and a half-headed arrow to the metal. */}
      {variant === 'agostic' ? (
        <>
          <line x1="4" y1="26" x2="11" y2="17" />
          <line x1="8" y1="21" x2="25" y2="9" />
          <polyline points="18,8 25,9 22,15" fill="none" strokeOpacity="0" />
          <line x1="25" y1="9" x2="18.5" y2="8.2" />
        </>
      ) : null}
      {variant === 'chain' ? <polyline points="4,22 12,10 20,22 28,10" fill="none" /> : null}
      {variant === 'solid-wedge' ? <polygon points="8,8 21,27 27,21" /> : null}
      {variant === 'hashed-wedge' ? (
        <>
          <line x1="8" y1="8" x2="24" y2="24" strokeOpacity="0" />
          <line x1="9.3" y1="11.5" x2="11.5" y2="9.3" />
          <line x1="12.5" y1="15.5" x2="15.5" y2="12.5" />
          <line x1="15.6" y1="19.6" x2="19.6" y2="15.6" />
          <line x1="18.7" y1="23.7" x2="23.7" y2="18.7" />
        </>
      ) : null}
    </svg>
  );
}
